// src/services/leavePlan.service.js
import { api } from './api';

class LeavePlanService {
    /**
     * Get all leave plans
     */
    async getLeavePlans() {
        try {
            const response = await api.get('/api/leave-plans');
            return response.data.plans || [];
        } catch (error) {
            console.error('Error fetching leave plans:', error);
            throw error;
        }
    }

    /**
     * Get specific leave plan with details
     */
    async getLeavePlanById(id) {
        try {
            const response = await api.get(`/api/leave-plans/${id}`);
            return response.data.plan;
        } catch (error) {
            console.error('Error fetching leave plan:', error);
            throw error;
        }
    }

    /**
     * Create new leave plan
     */
    async createLeavePlan(planData) {
        try {
            const response = await api.post('/api/leave-plans', planData);
            return response.data.plan;
        } catch (error) {
            console.error('Error creating leave plan:', error);
            throw error;
        }
    }

    /**
     * Update leave plan
     */
    async updateLeavePlan(id, planData) {
        try {
            const response = await api.put(`/api/leave-plans/${id}`, planData);
            return response.data.plan;
        } catch (error) {
            console.error('Error updating leave plan:', error);
            throw error;
        }
    }

    /**
     * Delete leave plan
     */
    async deleteLeavePlan(id) {
        try {
            const response = await api.delete(`/api/leave-plans/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting leave plan:', error);
            throw error;
        }
    }

    /**
     * Add leave type to plan
     */
    async addLeaveType(planId, leaveType) {
        try {
            const response = await api.post(`/api/leave-plans/${planId}/types`, leaveType);
            return response.data.leaveType;
        } catch (error) {
            console.error('Error adding leave type:', error);
            throw error;
        }
    }

    /**
     * Update leave type
     */
    async updateLeaveType(typeId, leaveType) {
        try {
            const response = await api.put(`/api/leave-plans/types/${typeId}`, leaveType);
            return response.data.leaveType;
        } catch (error) {
            console.error('Error updating leave type:', error);
            throw error;
        }
    }

    /**
     * Delete leave type
     */
    async deleteLeaveType(typeId) {
        try {
            const response = await api.delete(`/api/leave-plans/types/${typeId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting leave type:', error);
            throw error;
        }
    }

    /**
     * Assign employees to leave plan
     */
    async assignEmployees(planId, employeeIds) {
        try {
            const response = await api.post(`/api/leave-plans/${planId}/assign-employees`, {
                employee_ids: employeeIds
            });
            return response.data;
        } catch (error) {
            console.error('Error assigning employees:', error);
            throw error;
        }
    }

    /**
     * Unassign employee from leave plan
     */
    async unassignEmployee(planId, employeeId) {
        try {
            const response = await api.delete(`/api/leave-plans/${planId}/employees/${employeeId}`);
            return response.data;
        } catch (error) {
            console.error('Error unassigning employee:', error);
            throw error;
        }
    }

    /**
     * Get predefined leave types
     */
    getPredefinedLeaveTypes() {
        return [
            {
                leave_type_name: 'Casual Leave',
                leave_code: 'CL',
                total_days: 12,
                color_code: '#3B82F6',
                carry_forward: true,
                max_carry_forward_days: 3,
                encashable: false,
                requires_approval: true,
                min_days_notice: 1,
                prorate_on_joining: true
            },
            {
                leave_type_name: 'Sick Leave',
                leave_code: 'SL',
                total_days: 10,
                color_code: '#10B981',
                carry_forward: false,
                max_carry_forward_days: 0,
                encashable: false,
                requires_approval: false,
                min_days_notice: 0,
                prorate_on_joining: true
            },
            {
                leave_type_name: 'Privilege Leave',
                leave_code: 'PL',
                total_days: 15,
                color_code: '#8B5CF6',
                carry_forward: true,
                max_carry_forward_days: 10,
                encashable: true,
                requires_approval: true,
                min_days_notice: 7,
                prorate_on_joining: true
            },
            {
                leave_type_name: 'Maternity Leave',
                leave_code: 'ML',
                total_days: 180,
                color_code: '#EC4899',
                carry_forward: false,
                max_carry_forward_days: 0,
                encashable: false,
                requires_approval: true,
                min_days_notice: 30,
                prorate_on_joining: false
            },
            {
                leave_type_name: 'Paternity Leave',
                leave_code: 'PAL',
                total_days: 7,
                color_code: '#6366F1',
                carry_forward: false,
                max_carry_forward_days: 0,
                encashable: false,
                requires_approval: true,
                min_days_notice: 7,
                prorate_on_joining: false
            },
            {
                leave_type_name: 'Marriage Leave',
                leave_code: 'MRL',
                total_days: 5,
                color_code: '#F59E0B',
                carry_forward: false,
                max_carry_forward_days: 0,
                encashable: false,
                requires_approval: true,
                min_days_notice: 15,
                prorate_on_joining: false
            },
            {
                leave_type_name: 'Bereavement Leave',
                leave_code: 'BL',
                total_days: 5,
                color_code: '#6B7280',
                carry_forward: false,
                max_carry_forward_days: 0,
                encashable: false,
                requires_approval: false,
                min_days_notice: 0,
                prorate_on_joining: false
            },
            {
                leave_type_name: 'Compensatory Off',
                leave_code: 'CO',
                total_days: 0,
                color_code: '#14B8A6',
                carry_forward: false,
                max_carry_forward_days: 0,
                encashable: false,
                requires_approval: true,
                min_days_notice: 1,
                prorate_on_joining: false
            }
        ];
    }
}

export default new LeavePlanService();
